import { GridColumns } from '@mui/x-data-grid';
import moment from 'moment';
import { TextEditInputCell } from '../../components/tables/custom-input-edit-cell';
import { formatHourDisplay } from '../../utils';

export const weekFilter = [
  {
    id: 0,
    label: 'Current Week',
    startDate: moment().subtract(moment().day(), 'days').startOf('day'),
    endDate: moment().subtract(moment().day(), 'days').add(6, 'days').endOf('day'),
  },
  {
    id: 1,
    label: 'Last Week',
    startDate: moment()
      .subtract(moment().day() + 7, 'days')
      .startOf('day'),
    endDate: moment()
      .subtract(moment().day() + 7, 'days')
      .add(6, 'days')
      .endOf('day'),
  },
  {
    id: 2,
    label: 'Current Month',
    startDate: moment().startOf('month'),
    endDate: moment().endOf('month'),
  },
  {
    id: 3,
    label: 'Last Month',
    startDate: moment().subtract(1, 'month').startOf('month'),
    endDate: moment().subtract(1, 'month').endOf('month'),
  },
];

export const summaryHours = [
  {
    label: 'Submitted',
    key: 'submitted',
  },
  {
    label: 'Billable',
    key: 'billable',
    peopleHide: true,
  },
  {
    label: 'Payroll',
    key: 'payroll',
    timesheetHide: true,
  },
  {
    label: 'Non-Payroll',
    key: 'nonPayroll',
    timesheetHide: true,
  },
  {
    label: 'Non-Billable',
    key: 'nonBillable',
    peopleHide: true,
  },
  {
    label: 'Overtime',
    key: 'overTime',
  },
  {
    label: 'Assets',
    key: 'assets',
    peopleHide: true,
  },
];

export const timesheetStatus = {
  Cloned: 'warning',
  Draft: 'default',
  Submitted: 'warning',
  Locked: 'info',
  CustomerApproved: 'success',
  CustomerRejected: 'error',
  Approved: 'success',
  Rejected: 'error',
  Payrolled: 'primary',
  Invoiced: 'info',
  Onhold: 'error',
  Recreated: 'success',
  PendingApproval: 'warning',
} as const;

export type ITimesheetStatus = keyof typeof timesheetStatus;
export type ITimesheetStatusValues = typeof timesheetStatus[ITimesheetStatus];

export const timesheetQueueStatus = {
  InQueue: 'warning',
  Success: 'success',
  Failed: 'error',
} as const;

export type ITimesheetQueueStatus = keyof typeof timesheetQueueStatus;
export type ITimesheetQueueStatusValues = typeof timesheetQueueStatus[ITimesheetQueueStatus];

export enum timesheetQueueStatuses {
  InQueue = 'In Queue',
  InQueueStatus = 'InQueue',
  Success = 'Success',
  Failed = 'Failed',
}
export type ITimesheetQueueStatuses = keyof typeof timesheetQueueStatuses;

export const postToJDEStatus = {
  Queue: 'warning',
  Posted: 'success',
  Success: 'success',
  Failed: 'error',
  Requeued: 'error',
} as const;

export type IPostToJDEStatus = keyof typeof postToJDEStatus;
export type IPostToJDEStatusValues = typeof postToJDEStatus[IPostToJDEStatus];

export const timesheetStatuses = {
  Draft: 'Draft',
  Submitted: 'Submitted',
  Cloned: 'Cloned',
  CustomerApproved: 'CustomerApproved',
  CustomerRejected: 'CustomerRejected',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Payrolled: 'Payrolled',
  Invoiced: 'Invoiced',
  Onhold: 'Onhold',
  Recreated: 'Recreated',
  PendingApproval: 'PendingApproval',
  ForceUpdate: 'Force Update',
  SplitTimesheet: 'Split Timesheet',
};

export enum BulkActions {
  Submitted = 'Bulk Submit',
  Rejected = 'Bulk Reject',
  Approved = 'Bulk Approve',
  ReOpened = 'Bulk Re-Open',
  Deleted = 'Bulk Delete',
}
interface ShiftTableColumnsProps {
  t: any; // replace any with the correct type for your t function
}

export const shiftTableColumns = ({ t }: ShiftTableColumnsProps): GridColumns => [
  {
    field: 'defaultHrs',
    headerName: t('PTO/Leave Hours') || '',
    width: 140,
    renderCell: (props) => (props.value ? formatHourDisplay(props.value) : '-'),
    renderEditCell: (params) => (
      <TextEditInputCell
        placeholder={t('PTO/Leave Hours') || ''}
        props={{
          ...params,
        }}
        disable
      />
    ),
  },
];

export enum ExceptionType {
  NORMAL_HOURS,
  TEN_HOURS_EXCEPTION,
  SIXTEEN_HOURS_EXCEPTION,
}

export enum ExceptionTypeValues {
  NORMAL_HOURS = '-',
  TEN_HOURS_EXCEPTION = 10,
  SIXTEEN_HOURS_EXCEPTION = 16,
}

export enum ExceptionTypeColor {
  'warning-exception' = 1,
  'error-exception',
}

export const enum TypeField {
  TEXT = 1,
  DATE = 2,
  SELECT = 3,
  USER = 4,
  STATUS = 5,
}

export const enum ShiftTypes {
  WORKING = '1',
  VACATION_FULL_DAY = '2',
  VACATION_HALF_DAY = '3',
  ABSENT = '4',
  TRAVEL = '5',
  STANDBY = '6',
  BEREAVEMENT_FULL_DAY = '7',
  WORKING_HOLIDAY = '8',
  PERSONAL_DAY = '9',
  HOLIDAY_COMPANY_FUNDED = '10',
  SICK = '11',
  SICK_MARYLAND_ONLY = '12',
  BEREAVEMENT_HALF_DAY = '13',
  HOLIDAY = '14',
  MA_SICK_LEAVE = '15',
  NY_SICK_LEAVE = '16',
  TRAINING = '17',
  GO_BACKS = '18',
}

export const EXPANDABLE_TABLE_ID = {
  ASSETS: 'assets',
  PRODUCTION: 'production',
  TIME_SUMMARY: 'time-summary',
  LOCATION: 'location',
  OTHER_BILLABLE: 'other-billable',
  ADDITIONAL_DATA: 'additional-data',
} as const;

export type ExpandableTableId = typeof EXPANDABLE_TABLE_ID[keyof typeof EXPANDABLE_TABLE_ID];
